import {REFRESH_TOKEN_EXPIRES_AT_COOKIE_NAME} from '~/conf/app.js';

export function useAuthGuard() {
  const {authRefreshTokenMinValidityMs} = useRuntimeConfig().public;

  async function tryRefresh(signInUrl, {redirect = true} = {}) {
    const {refreshToken} = useTokenRefresh();
    try {
      await refreshToken();
    } catch (e) {
      if (e.response?.status === 401 || e.status === 401) {
        if (redirect) {
          await navigateTo(signInUrl, {external: true});
          return true;
        }
        return false;
      }
      throw e;
    }
    return true;
  }

  // The refresh_token cookie is httpOnly; its readable companion is set and cleared
  // alongside it with the same expiry, so it stands in for "a refresh token exists".
  function hasRefreshToken() {
    const expiresAt = useCookie(REFRESH_TOKEN_EXPIRES_AT_COOKIE_NAME).value;
    return Boolean(expiresAt) && new Date(expiresAt).getTime() > Date.now();
  }

  function hasRefreshTokenMinValidity() {
    const expiresAt = useCookie(REFRESH_TOKEN_EXPIRES_AT_COOKIE_NAME).value;
    if (!expiresAt) return false;
    return new Date(expiresAt).getTime() - Date.now() > authRefreshTokenMinValidityMs;
  }

  async function ensureValidSession(originUrl, {redirect = true} = {}) {
    const {userInfoCookie, authState, isTokenExpired} = useAuthCookie();
    const {buildSignInUrl} = useAuthHost();
    const signInUrl = buildSignInUrl(originUrl);

    if (process.server && authState.value?.auth_token && !isTokenExpired(authState.value)) {
      return true;
    }

    if (userInfoCookie.value?.auth_token) {
      if (!isTokenExpired(userInfoCookie.value)) {
        if (process.server) {
          authState.value = userInfoCookie.value;
        }
        return true;
      }
      return tryRefresh(signInUrl, {redirect});
    }

    if (hasRefreshTokenMinValidity()) {
      return tryRefresh(signInUrl, {redirect});
    }

    if (redirect) {
      await navigateTo(signInUrl, {external: true});
      return true;
    }

    return false;
  }

  return {hasRefreshToken, hasRefreshTokenMinValidity, ensureValidSession};
}
